import type { Emf } from 'eolib';
import type { Client } from '@/client';
import { getEmf } from '@/db';
import { GfxType } from '@/gfx';
import { GameState } from '@/game-state';
import { LAYER_GFX_MAP } from '@/map';
import { isPrivateMapId } from '@/private-map';
import type { Vector2 } from '@/vector';

const PRELOAD_DISTANCE = 10;
const MAX_DESTINATIONS = 2;
const MAX_GRAPHICS = 128;

export function nearbyWarpDestinations(map: Emf, currentMapId: number, position: Vector2, level: number): number[] {
  const distances = new Map<number, number>();
  for (const row of map.warpRows) for (const tile of row.tiles) {
    const id = tile.warp.destinationMap;
    const distance = Math.abs(tile.x - position.x) + Math.abs(row.y - position.y);
    if (id <= 0 || id === currentMapId || isPrivateMapId(id) ||
        tile.warp.levelRequired > level || distance > PRELOAD_DISTANCE) continue;
    distances.set(id, Math.min(distance, distances.get(id) ?? Infinity));
  }
  return [...distances].sort((a, b) => a[1] - b[1]).slice(0, MAX_DESTINATIONS).map(([id]) => id);
}

export function mapGraphicsToWarm(map: Emf): Array<[number, number]> {
  const graphics = new Map<string, [number, number]>();
  const add = (file: number, graphic: number) => {
    if (file !== undefined && graphic > 0 && graphics.size < MAX_GRAPHICS) {
      graphics.set(`${file}:${graphic}`, [file, graphic + 100]);
    }
  };
  add(GfxType.MapTiles, map.fillTile);
  for (const [index, layer] of map.graphicLayers.entries()) {
    for (const row of layer.graphicRows) for (const tile of row.tiles) add(LAYER_GFX_MAP[index], tile.graphic);
  }
  return [...graphics.values()];
}

/** Speculative cache warming only: never installs a map or repacks the active atlas. */
export class WarpPreloader {
  private busy = false;
  private nextCheck = 0;
  private attempts = new Map<number, number>();
  constructor(private client: Client) {}

  tick(): void {
    const client = this.client;
    const now = Date.now();
    if (this.busy || client.worldLoading || !client.map || now < this.nextCheck) return;
    this.nextCheck = now + 1500;
    // Save-data users still get the held-frame transition without speculative traffic.
    if ((navigator as Navigator & {connection?: {saveData?: boolean}}).connection?.saveData) return;
    const id = nearbyWarpDestinations(client.map, client.mapId, client.getPlayerCoords(), client.level)
      .find(id => now - (this.attempts.get(id) ?? 0) >= 60000);
    if (!id) return;
    this.attempts.delete(id);
    this.attempts.set(id, now);
    if (this.attempts.size > 32) this.attempts.delete(this.attempts.keys().next().value!);
    const source = client.map;
    this.busy = true;
    void this.warm(id, source).catch(() => {
      // A failed speculative read must never affect the real warp/retry path.
    }).finally(() => { this.busy = false; });
  }

  private async warm(id: number, source: Emf): Promise<void> {
    const client = this.client;
    const map = await getEmf(id);
    if (!map || client.map !== source || client.worldLoading || client.state !== GameState.InGame) return;
    const graphics = mapGraphicsToWarm(map);
    // Small sequential batches leave the decoder available for active gameplay.
    for (let i = 0; i < graphics.length; i += 4) {
      if (client.map !== source || client.worldLoading || client.state !== GameState.InGame) return;
      await Promise.all(graphics.slice(i, i + 4).map(([file, resource]) =>
        client.gfxLoader.preloadCachedResource(file, resource).catch(() => {})));
    }
  }
}
